import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, X, Plus, Loader2, CheckCircle, Sparkles, FileText, Eye, EyeOff, Linkedin, Globe } from 'lucide-react';
import { useKB } from './KBContext';

const fieldLabel = {
  fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
  color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 8, display: 'block',
};
const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: '1px solid var(--kb-border)', background: 'var(--kb-bg-soft, rgba(255,255,255,0.03))',
  color: 'var(--kb-fg)', fontSize: 14, outline: 'none', boxSizing: 'border-box',
};

const Toggle = ({ checked, onChange, label, hint, testId }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    data-testid={testId}
    style={{
      display: 'flex', alignItems: 'center', gap: 12, width: '100%',
      background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer', textAlign: 'left',
    }}
  >
    <span style={{
      width: 38, height: 22, borderRadius: 999, flexShrink: 0, position: 'relative',
      background: checked ? 'var(--kb-accent, #2563EB)' : 'var(--kb-border)',
      transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: checked ? 18 : 2, width: 18, height: 18,
        borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </span>
    <span style={{ flex: 1 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: 'var(--kb-fg)', fontWeight: 500 }}>
        {checked ? <EyeOff size={13} /> : <Eye size={13} />} {label}
      </span>
      {hint && <span style={{ fontSize: 12, color: 'var(--kb-fg-soft, #8B96A5)' }}>{hint}</span>}
    </span>
  </button>
);

const ProfileEditModal = ({ show, onClose }) => {
  const { memberInfo, updateMemberProfile } = useKB();
  const [bio, setBio] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [website, setWebsite] = useState('');
  const [hideEmail, setHideEmail] = useState(false);
  const [hidePhone, setHidePhone] = useState(false);
  const [hideCompany, setHideCompany] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const tagRef = useRef(null);

  // Hydrate from memberInfo each time the modal opens
  useEffect(() => {
    if (show && memberInfo) {
      setBio(memberInfo.bio || '');
      setTags(memberInfo.tags || []);
      setLinkedin(memberInfo.links?.linkedin || '');
      setWebsite(memberInfo.links?.website || '');
      setHideEmail(!!memberInfo.privacy?.hide_email);
      setHidePhone(!!memberInfo.privacy?.hide_phone);
      setHideCompany(!!memberInfo.privacy?.hide_company);
      setError(''); setSaved(false); setTagInput('');
    }
  }, [show, memberInfo]);

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) { setTagInput(''); return; }
    if (tags.length >= 20) return;
    setTags([...tags, t]);
    setTagInput('');
  };

  const removeTag = (i) => setTags(tags.filter((_, idx) => idx !== i));

  const onTagKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
    else if (e.key === 'Backspace' && !tagInput && tags.length) { removeTag(tags.length - 1); }
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSaved(false);
    const res = await updateMemberProfile({
      bio, tags, linkedin, website,
      hide_email: hideEmail, hide_phone: hidePhone, hide_company: hideCompany,
    });
    setSaving(false);
    if (res.ok) {
      setSaved(true);
      setTimeout(() => { setSaved(false); onClose(); }, 900);
    } else {
      setError(res.error || 'Could not save changes.');
    }
  };

  if (!memberInfo) return null;
  const expertise = memberInfo.expertise_topics || [];
  const authored = memberInfo.authored_doc_count || 0;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="kb-disclaimer-overlay"
          onClick={onClose}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="kb-profile-overlay"
        >
          <motion.div
            className="kb-disclaimer-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            data-testid="kb-profile-modal"
            style={{ maxWidth: 560, width: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="kb-disclaimer-header">
              <User size={18} />
              <h3>Edit My Profile</h3>
              <button className="kb-disclaimer-close" onClick={onClose} data-testid="kb-profile-close">
                <X size={18} />
              </button>
            </div>

            <div className="kb-disclaimer-body" style={{ overflowY: 'auto', padding: '20px 24px' }}>
              {/* Identity (read-only, from Glue Up) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
                {memberInfo.photo_url ? (
                  <img src={memberInfo.photo_url} alt={memberInfo.full_name}
                    style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--kb-accent, #2563EB)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 }}>
                    {(memberInfo.full_name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
                  </div>
                )}
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--kb-fg)' }}>{memberInfo.full_name}</div>
                  {memberInfo.position_title && (
                    <div style={{ fontSize: 13, color: 'var(--kb-fg-soft, #8B96A5)' }}>{memberInfo.position_title}</div>
                  )}
                  {memberInfo.company_name && (
                    <div style={{ fontSize: 13, color: 'var(--kb-fg-soft, #8B96A5)' }}>{memberInfo.company_name}</div>
                  )}
                </div>
              </div>

              {/* Bio */}
              <div style={{ marginBottom: 20 }}>
                <label style={fieldLabel}>About / Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 1200))}
                  placeholder="Tell other members about your background, focus areas, and what you're working on…"
                  data-testid="kb-profile-bio"
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                />
                <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--kb-fg-soft, #8B96A5)', marginTop: 4 }}>
                  {bio.length}/1200
                </div>
              </div>

              {/* Expertise tags */}
              <div style={{ marginBottom: 20 }}>
                <label style={fieldLabel}>Specialties / Tags</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                  {tags.map((t, i) => (
                    <span key={i} data-testid={`kb-profile-tag-${i}`} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px',
                      borderRadius: 999, background: 'rgba(37,99,235,0.10)', border: '1px solid rgba(37,99,235,0.3)',
                      color: 'var(--kb-accent, #2563EB)', fontSize: 13, fontWeight: 500,
                    }}>
                      {t}
                      <button type="button" onClick={() => removeTag(i)} data-testid={`kb-profile-tag-remove-${i}`}
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex' }}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    ref={tagRef}
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={onTagKey}
                    placeholder="Add a specialty and press Enter (e.g. Rhodium Refining)"
                    data-testid="kb-profile-tag-input"
                    style={{ ...inputStyle, flex: 1 }}
                    maxLength={40}
                  />
                  <button type="button" onClick={addTag} data-testid="kb-profile-tag-add"
                    style={{ ...inputStyle, width: 'auto', padding: '0 14px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                    <Plus size={15} /> Add
                  </button>
                </div>
              </div>

              {/* AI-inferred expertise (read-only) */}
              {(expertise.length > 0 || authored > 0) && (
                <div style={{ marginBottom: 20, padding: '12px 14px', borderRadius: 10,
                  background: 'var(--kb-bg-soft, rgba(255,255,255,0.03))', border: '1px solid var(--kb-border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
                    color: 'var(--kb-fg-soft, #8B96A5)', marginBottom: 8 }}>
                    <Sparkles size={13} /> AI-inferred from your activity
                    {authored > 0 && (
                      <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        data-testid="kb-profile-authored-count">
                        <FileText size={12} /> {authored} authored {authored === 1 ? 'document' : 'documents'}
                      </span>
                    )}
                  </div>
                  {expertise.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {expertise.slice(0, 12).map((t, i) => (
                        <span key={i} style={{ padding: '3px 9px', borderRadius: 999, fontSize: 12,
                          background: 'var(--kb-border)', color: 'var(--kb-fg-soft, #8B96A5)' }}>{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Contact links */}
              <div style={{ marginBottom: 20 }}>
                <label style={fieldLabel}>Contact Links</label>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <Linkedin size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--kb-fg-soft, #8B96A5)' }} />
                  <input value={linkedin} onChange={(e) => setLinkedin(e.target.value)}
                    placeholder="LinkedIn profile URL" data-testid="kb-profile-linkedin"
                    style={{ ...inputStyle, paddingLeft: 36 }} />
                </div>
                <div style={{ position: 'relative' }}>
                  <Globe size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--kb-fg-soft, #8B96A5)' }} />
                  <input value={website} onChange={(e) => setWebsite(e.target.value)}
                    placeholder="Website URL" data-testid="kb-profile-website"
                    style={{ ...inputStyle, paddingLeft: 36 }} />
                </div>
              </div>

              {/* Privacy */}
              <div style={{ marginBottom: 4 }}>
                <label style={fieldLabel}>Privacy — hide from the member directory</label>
                <Toggle checked={hideEmail} onChange={setHideEmail} label="Hide my email" testId="kb-profile-hide-email" />
                <Toggle checked={hidePhone} onChange={setHidePhone} label="Hide my phone" testId="kb-profile-hide-phone" />
                <Toggle checked={hideCompany} onChange={setHideCompany} label="Hide my company" testId="kb-profile-hide-company" />
              </div>

              {error && (
                <div data-testid="kb-profile-error" style={{ marginTop: 12, color: '#EF4444', fontSize: 13 }}>{error}</div>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid var(--kb-border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" onClick={onClose} data-testid="kb-profile-cancel"
                style={{ ...inputStyle, width: 'auto', padding: '10px 18px', cursor: 'pointer', background: 'transparent' }}>
                Cancel
              </button>
              <button type="button" onClick={handleSave} disabled={saving || saved} data-testid="kb-profile-save"
                style={{ width: 'auto', padding: '10px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: saved ? '#10B981' : 'var(--kb-accent, #2563EB)', color: '#fff', fontWeight: 600, fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 8 }}>
                {saving ? <><Loader2 size={15} className="kb-spin" /> Saving…</>
                  : saved ? <><CheckCircle size={15} /> Saved</>
                  : 'Save changes'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileEditModal;
